import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { setLangFromPath, isEnPath, localePath, readLangPref, saveLangPref, browserPrefersZh, isBot, lang } from '@/i18n'
import { HOME_META, SERVICES } from '@/content-pages.mjs'
import { trackPageview } from '@/track'

import { PAGE_META } from '@/content-solutions.mjs'
import { USDT_TOOL } from '@/content-tools.mjs'
import { CHECKLIST } from '@/content-checklist.mjs'
import { RESEARCH_PAGE, findArticle } from '@/content-research.mjs'

const ResearchView = () => import('../views/ResearchView.vue')
const ArticleView = () => import('../views/ArticleView.vue')
const ServiceView = () => import('../views/ServiceView.vue')
const PricingView = () => import('../views/PricingView.vue')
const SolutionsView = () => import('../views/SolutionsView.vue')
const ToolView = () => import('../views/ToolView.vue')
const ChecklistView = () => import('../views/ChecklistView.vue')

const routes = [
  { path: '/', name: 'home', component: HomeView },
  { path: '/en', name: 'home-en', component: HomeView },
  { path: '/pricing', name: 'pricing', component: PricingView, meta: { page: 'pricing' } },
  { path: '/en/pricing', name: 'pricing-en', component: PricingView, meta: { page: 'pricing' } },
  { path: '/solutions', name: 'solutions', component: SolutionsView, meta: { page: 'solutions' } },
  { path: '/en/solutions', name: 'solutions-en', component: SolutionsView, meta: { page: 'solutions' } },
  { path: '/tools/usdt-check', name: 'tool', component: ToolView, meta: { page: 'usdt' } },
  { path: '/en/tools/usdt-check', name: 'tool-en', component: ToolView, meta: { page: 'usdt' } },
  { path: '/checklist', name: 'checklist', component: ChecklistView, meta: { page: 'checklist' } },
  { path: '/en/checklist', name: 'checklist-en', component: ChecklistView, meta: { page: 'checklist' } },
  { path: '/services/:slug', name: 'service', component: ServiceView },
  { path: '/en/services/:slug', name: 'service-en', component: ServiceView },
  { path: '/research', name: 'research', component: ResearchView, meta: { page: 'research' } },
  { path: '/en/research', name: 'research-en', component: ResearchView, meta: { page: 'research' } },
  { path: '/research/:slug', name: 'article', component: ArticleView, meta: { page: 'article' } },
  { path: '/en/research/:slug', name: 'article-en', component: ArticleView, meta: { page: 'article' } },

  // 旧链接兼容
  { path: '/home', redirect: to => ({ path: '/', query: to.query, hash: to.hash }) },
  { path: '/about', redirect: { path: '/', hash: '#about' } },
  { path: '/service', redirect: '/solutions/' },
  { path: '/product', redirect: '/pricing/' },
  { path: '/en/:pathMatch(.*)*', redirect: '/en/' },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, saved) {
    if (saved) return saved
    // 同一页面只改查询参数（如安全研究的标签筛选 ?tag=）时保持滚动位置
    if (from.matched.length && to.path === from.path && !to.hash) return false
    if (to.hash) {
      // 跨页面跳到锚点用瞬时滚动：页面切换时平滑滚动会被打断，停在旧位置
      const behavior = to.path === from.path ? 'smooth' : 'instant'
      return new Promise(resolve => requestAnimationFrame(() => resolve({ el: to.hash, top: 68, behavior })))
    }
    return { top: 0, behavior: 'instant' }
  },
})

let first = true
router.beforeEach(to => {
  // 1) ?lang=en / ?lang=zh：强制语言并记住（广告链接可以直接带这个参数）
  const q = to.query.lang
  if (q === 'en' || q === 'zh') {
    saveLangPref(q)
    const query = { ...to.query }
    delete query.lang
    return { path: localePath(to.path, q), query, hash: to.hash, replace: true }
  }
  // 2) 首次打开中文网址：按记住的选择或浏览器语言自动切到英文（爬虫除外）
  if (first) {
    first = false
    if (!isEnPath(to.path) && !isBot()) {
      const pref = readLangPref()
      const wantEn = pref ? pref === 'en' : !browserPrefersZh()
      if (wantEn) return { path: localePath(to.path, 'en'), query: to.query, hash: to.hash, replace: true }
    }
  }
  // 服务页 slug 不存在时回首页
  if ((to.name === 'service' || to.name === 'service-en') && !SERVICES.some(s => s.slug === to.params.slug)) {
    return { path: localePath('/', isEnPath(to.path) ? 'en' : 'zh'), replace: true }
  }
  // 文章 slug 不存在时回安全研究列表
  if ((to.name === 'article' || to.name === 'article-en') && !findArticle(to.params.slug)) {
    return { path: localePath('/research/', isEnPath(to.path) ? 'en' : 'zh'), replace: true }
  }
})

router.afterEach(to => {
  setLangFromPath(to.path)
  // 页面标题和描述（搜索引擎渲染 JS 时也会读到）
  const l = lang.value
  let title = HOME_META[l].title
  let desc = HOME_META[l].description
  const svc = SERVICES.find(s => s.slug === to.params.slug)
  if (svc) { title = svc[l].metaTitle; desc = svc[l].metaDesc }
  const pm = to.meta.page && PAGE_META[to.meta.page]
  if (pm) { title = pm[l].title; desc = pm[l].description }
  if (to.meta.page === 'usdt') { title = USDT_TOOL[l].metaTitle; desc = USDT_TOOL[l].metaDesc }
  if (to.meta.page === 'checklist') { title = CHECKLIST[l].metaTitle; desc = CHECKLIST[l].metaDesc }
  if (to.meta.page === 'research') { title = RESEARCH_PAGE[l].metaTitle; desc = RESEARCH_PAGE[l].metaDesc }
  const art = to.meta.page === 'article' && findArticle(to.params.slug)
  if (art) { title = art[l].metaTitle; desc = art[l].desc }
  document.title = title
  const m = document.querySelector('meta[name="description"]')
  if (m) m.setAttribute('content', desc)
  trackPageview(to.fullPath, title)
})

export default router
